import { useState } from "react";
import { X, Clock, MessageSquare, Play } from "lucide-react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAgentConversations } from "./useAgentConversations";
import { format } from "date-fns";
import { he, enUS } from "date-fns/locale";
import { Spinner } from "@/components/flowly/infra/Spinner/Spinner";
import { EmptyState } from "@/components/flowly/infra/EmptyState/EmptyState";
import { Button } from "@/components/flowly/infra/Button/Button";

interface HistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectConversation?: (conversationId: string) => void;
  onResumeConversation?: (conversationId: string) => void;
}

export const HistoryPanel = ({ 
  isOpen, 
  onClose, 
  onSelectConversation,
  onResumeConversation,
}: HistoryPanelProps) => {
  const { language, t } = useLanguage();
  const { conversations, loading } = useAgentConversations();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSelect = (id: string) => {
    setSelectedId(id);
    onSelectConversation?.(id);
  };

  const handleResume = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onResumeConversation?.(id);
    onClose();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return format(date, "dd MMM, HH:mm", { 
      locale: language === "he" ? he : enUS
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className={cn(
          "absolute top-0 h-full w-full max-w-md bg-card border-border shadow-2xl animate-slide-in-left",
          "start-0 border-e"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-bold text-foreground">
              {t.historyPanel.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-secondary hover:bg-muted flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Conversations List */}
        <div className="overflow-y-auto h-[calc(100%-64px)] p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Spinner size="md" className="mb-3" />
              <p className="text-sm">{t.historyPanel.loading}</p>
            </div>
          ) : conversations.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title={t.historyPanel.empty}
              className="py-12"
            />
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => handleSelect(conv.id)}
                  className={cn(
                    "w-full p-4 rounded-xl border transition-all text-start cursor-pointer",
                    selectedId === conv.id
                      ? "border-primary bg-primary/5"
                      : "border-border bg-secondary/50 hover:bg-secondary hover:border-primary/30"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm truncate">
                        {conv.title || t.historyPanel.untitled}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(conv.createdAt)}
                      </p>
                    </div>
                    
                    {/* Resume Button */}
                    {onResumeConversation && (
                      <Button
                        variant="primary"
                        size="xs"
                        rounded="lg"
                        onClick={(e) => handleResume(conv.id, e)}
                      >
                        <Play className="w-3 h-3" />
                        {t.historyPanel.resume}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
